import { PubSub } from '@aws-amplify/pubsub';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useEffect, useRef } from 'react';
import { REGION } from 'src/models/globals';

/** WebSocket connection status values. */
export const WS_STATUS = {
	CONNECTED: 'connected',
	DISCONNECTED: 'disconnected',
	RECONNECTING: 'reconnecting',
};

/**
 * Determines whether to use WebSocket (true) or IoT PubSub (false) based on region and environment.
 */
const USE_WEBSOCKET = REGION === 'af-south-1' || process.env.REACT_APP_ENV === 'dev';

/**
 * Custom hook that abstracts the transport layer for preview dialing.
 * Handles call status subscriptions, placing dials, and contact actions (approve/recycle/suppress).
 *
 * - IoT PubSub: Used in most regions. Publishes actions to IoT topics and subscribes via MQTT.
 * - WebSocket: Used in af-south-1 and dev. Calls REST APIs directly and subscribes via native WebSocket.
 *
 * @param {Object} params
 * @param {string} params.agentId - The agent's ID.
 * @param {string} params.tenantId - The tenant ID.
 * @param {string} params.apiKey - The API key for REST calls.
 * @param {function} params.onCallStatusChange - Callback when call status changes.
 * @param {function} params.onAllowDial - Callback when allow-dial message is received (IoT/Webex only).
 * @param {function} params.onConnectionStatusChange - Callback when WebSocket connection status changes (connected/disconnected/reconnecting/failed).
 */
export function useDialTransport({ agentId, tenantId, apiKey, onCallStatusChange, onAllowDial, onConnectionStatusChange }) {
	const callStatusSubscription = useRef(null);
	const allowDialSubscription = useRef(null);
	const intentionalClose = useRef(false);
	const onConnectionStatusChangeRef = useRef(onConnectionStatusChange);
	const onCallStatusChangeRef = useRef(onCallStatusChange);

	// Keep refs in sync with latest callbacks
	useEffect(() => { onConnectionStatusChangeRef.current = onConnectionStatusChange; }, [onConnectionStatusChange]);
	useEffect(() => { onCallStatusChangeRef.current = onCallStatusChange; }, [onCallStatusChange]);

	const pubsubRef = useRef(
		!USE_WEBSOCKET
			? new PubSub({
				region: REGION,
				endpoint: `wss://a3b8mw0zsvy66t-ats.iot.${REGION}.amazonaws.com/mqtt`,
			})
			: null,
	);

	/**
	 * Subscribes to call status updates.
	 */
	const subscribeToCallStatus = useCallback(() => {
		if (USE_WEBSOCKET) {
			subscribeToCallStatusWS();
		} else {
			subscribeToCallStatusIoT();
		}
	}, [agentId, tenantId]);

	function subscribeToCallStatusIoT() {
		try {
			callStatusSubscription.current?.unsubscribe();
			callStatusSubscription.current = pubsubRef.current
				.subscribe({
					topics: `approvals/${process.env.REACT_APP_ENV}/${tenantId}/agent-call-statuses/${agentId}`,
				})
				.subscribe({
					next: (message) => onCallStatusChangeRef.current?.(message),
					error: (error) => console.error('Error subscribing to call status (IoT)', error),
					complete: () => console.log('Call Status IoT subscription complete'),
				});
		} catch (error) {
			console.error('Error subscribing to call status (IoT)', error);
		}
	}

	function subscribeToCallStatusWS(retryCount = 0) {
		try {
			// Detach handlers from the previous connection so its close event doesn't trigger reconnection
			if (callStatusSubscription.current) {
				callStatusSubscription.current.onclose = null;
				callStatusSubscription.current.onerror = null;
				callStatusSubscription.current.onmessage = null;
				callStatusSubscription.current.close?.();
			}

			// Reset intentionalClose for the new connection (handles React strict mode double-mount)
			intentionalClose.current = false;

			const wsUrl = `wss://${process.env.REACT_APP_ENV}.ws.customerdynamics.com?agentId=${agentId}`;
			const ws = new WebSocket(wsUrl);
			callStatusSubscription.current = ws;

			if (retryCount > 0) {
				onConnectionStatusChangeRef.current?.(WS_STATUS.RECONNECTING, retryCount);
			}

			ws.onopen = () => {
				console.log('Call Status WebSocket connection established');
				// DEV ONLY: expose for testing disconnects via console: window.__testWS.close()
				if (process.env.REACT_APP_ENV === 'dev') {
					window.__testWS = ws;
				}
				if (retryCount > 0) {
					onConnectionStatusChangeRef.current?.(WS_STATUS.CONNECTED);
				}
			};

			ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data);
					onCallStatusChangeRef.current?.(message);
				} catch (err) {
					console.error('Error parsing call status message', err);
				}
			};

			ws.onerror = (error) => {
				console.error('WebSocket error', error);
			};

			ws.onclose = () => {
				console.log('Call Status WebSocket connection closed');

				// Don't reconnect if the close was triggered by cleanup
				if (intentionalClose.current) {
					return;
				}

				onConnectionStatusChangeRef.current?.(WS_STATUS.DISCONNECTED);

				const delay = Math.min(2000 * (retryCount + 1), 30000);
				console.log(`Reconnecting WebSocket... attempt ${retryCount + 1} (next in ${delay}ms)`);
				setTimeout(() => subscribeToCallStatusWS(retryCount + 1), delay);
			};
		} catch (error) {
			console.error('Error subscribing to call status (WebSocket)', error);
			const delay = Math.min(2000 * (retryCount + 1), 30000);
			setTimeout(() => subscribeToCallStatusWS(retryCount + 1), delay);
		}
	}

	/**
	 * Subscribes to the allow-dial topic (IoT only, used for Webex).
	 */
	const subscribeToAllowDial = useCallback(() => {
		if (USE_WEBSOCKET) return; // Not needed for WebSocket transport
		allowDialSubscription.current?.unsubscribe();
		const baseTopic = `approvals/${process.env.REACT_APP_ENV}/${tenantId}`;
		allowDialSubscription.current = pubsubRef.current
			.subscribe({
				topics: `${baseTopic}/c2c/${agentId}`,
			})
			.subscribe({
				next: (message) => onAllowDial?.(message),
				error: (error) => console.error('Error in agent allow dial subscription', error),
				complete: () => console.log('Allow dial subscription closed.'),
			});
	}, [agentId, tenantId, onAllowDial]);

	/**
	 * Places a dial or performs a contact action (approve/recycle/suppress).
	 *
	 * IoT: publishes the action message to the campaign topic.
	 * WebSocket: calls the REST API directly for 'approve', or the disposition API for 'suppress'.
	 *
	 * @param {Object} params
	 * @param {string} params.type - 'approve', 'recycle', or 'suppress'
	 * @param {Object} params.contactHash - The contact hash object.
	 * @param {Object} params.activeContact - The full contact object.
	 * @param {string} params.phoneNumber - The phone number to dial.
	 * @param {Object} params.campaign - The selected campaign.
	 * @param {string} params.suppressionProfileId - The suppression profile ID.
	 */
	const performContactAction = useCallback(async ({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId }) => {
		if (USE_WEBSOCKET) {
			return performContactActionWS({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId });
		} else {
			return performContactActionIoT({ type, contactHash, phoneNumber, campaign });
		}
	}, [tenantId, agentId, apiKey]);

	async function performContactActionIoT({ type, contactHash, phoneNumber, campaign }) {
		contactHash.phone = phoneNumber;
		await pubsubRef.current.publish({
			topics: [
				`approvals/${process.env.REACT_APP_ENV}/${tenantId}/${campaign.id}`,
			],
			message: {
				type,
				skillId: campaign.skillId,
				agent: agentId,
				cci: campaign.cci,
				...contactHash,
				phone: phoneNumber,
				jwtToken: (await fetchAuthSession()).tokens.idToken,
				apiKey,
				preview: true,
			},
		});
	}

	async function performContactActionWS({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId }) {
		const session = await fetchAuthSession();
		const idToken = session.tokens.idToken;

		if (type === 'approve') {
			// Call the /dial endpoint directly
			const dialResult = await post({
				apiName: 'cdyxoutreach',
				path: '/dial',
				options: {
					headers: {
						Authorization: `Bearer ${idToken}`,
						'x-api-key': apiKey,
					},
					body: {
						contact: { ...activeContact, phone: phoneNumber },
						field: 'phone',
						dialingProfileId: campaign.campaignProfileId,
						skillId: campaign.skillId,
						agentId,
					},
				},
			}).response;

			const dialResponse = await dialResult.body.json();
			if (!dialResponse.contactId) {
				throw new Error('Error dialing contact');
			}
		} else if (type === 'suppress') {
			// Call the /disposition endpoint
			await post({
				apiName: 'cdyxoutreach',
				path: '/disposition',
				options: {
					headers: {
						Authorization: `Bearer ${idToken}`,
						'x-api-key': apiKey,
					},
					body: {
						additionalInfo: {
							id: contactHash.id,
							campaignId: contactHash.campaignId,
							segmentId: contactHash.segmentId,
						},
						agent: agentId,
						icUserName: agentId,
						icContactId: '',
						dispClassId: 41,
						cciDispId: 41,
						dispCode: 41,
						dispClassName: 'Contact Suppressed - Preview Dialing',
						dispDescription: 'The contact was suppressed from dialing in preview dialing mode.',
						phoneNumber,
						timeInitiated: new Date().toISOString(),
						timeDisposed: new Date().toISOString(),
						timeDisconnected: new Date().toISOString(),
						id: contactHash.id,
						campaignId: contactHash.campaignId,
						segmentId: contactHash.segmentId,
						crmContactId: contactHash.externalId,
						source: 'Preview Dial',
					},
				},
			});
		}
		// 'recycle' in WS mode is effectively a no-op (contact is just dismissed)
	}

	/**
	 * Checks if a contact is valid for dialing.
	 *
	 * @param {Object} params
	 * @param {Object} params.contact - The contact to check.
	 * @param {string} params.phoneNumber - The phone number being dialed.
	 * @param {string} params.profileId - The campaign/dialing profile ID.
	 * @param {string} [params.suppressionProfileId] - Explicit suppression profile ID (manual dialing).
	 * @param {string} [params.callerIdConfigurationId] - Caller ID configuration to assign (e.g. from a manual profile).
	 * @param {string} [params.campaignId] - Campaign id used to resolve caller ID (and, with useCampaignSuppression, suppression) config (e.g. callbacks).
	 * @param {boolean} [params.useCampaignSuppression] - When true, resolve suppression from the contact's segment (campaign suppression, e.g. callbacks).
	 * @returns {Promise<{passed: boolean, message: string, contact: Object}>} passed/message plus the checked contact (with any assigned outboundCallerId).
	 */
	const checkContact = useCallback(async ({ contact, phoneNumber, profileId, suppressionProfileId, callerIdConfigurationId, campaignId, useCampaignSuppression }) => {
		const checkResult = await post({
			apiName: 'cdyxoutreach',
			path: '/dial/check',
			options: {
				headers: {
					Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
					'x-api-key': apiKey,
				},
				body: {
					contact: { ...contact, phone: phoneNumber },
					field: 'phone',
					profileId,
					tenantId,
					suppressionProfileId,
					callerIdConfigurationId,
					campaignId,
					useCampaignSuppression,
				},
			},
		}).response;

		const checkResponse = await checkResult.body.json();
		const passed = checkResponse?.Message === 'Contact passed check';
		return {
			passed,
			message: passed
				? checkResponse.Message
				: checkResponse?.ErrorMessage || checkResponse?.Message || 'Error checking contact',
			// The check returns the (possibly mutated) contact, e.g. with an
			// assigned outboundCallerId, so callers can dial the checked contact.
			contact: checkResponse?.contact,
		};
	}, [tenantId, apiKey]);

	/**
	 * Cleans up all subscriptions.
	 */
	const cleanup = useCallback(() => {
		if (USE_WEBSOCKET) {
			intentionalClose.current = true;
			callStatusSubscription.current?.close?.();
		} else {
			callStatusSubscription.current?.unsubscribe();
			allowDialSubscription.current?.unsubscribe();
		}
	}, []);

	// Auto-cleanup on unmount
	useEffect(() => {
		return () => cleanup();
	}, []);

	return {
		/** Whether the transport is using WebSocket (true) or IoT (false). */
		isWebSocket: USE_WEBSOCKET,
		subscribeToCallStatus,
		subscribeToAllowDial,
		performContactAction,
		checkContact,
		cleanup,
		/** The PubSub instance (null if using WebSocket). Exposed for Webex outdial. */
		pubsub: pubsubRef.current,
	};
}
